// @ts-strict-ignore
import { v4 as uuidv4 } from 'uuid';
import type { FlowState } from '~/flow/types';
import type { ServiceConfig } from '~/dashboard/views/settings/service-configuration/types';
import buildDependencies from '../../engine/dependencies';

import type {
  WorkflowOperationMessage, ServiceCredentials, ServiceObject, WorkflowPropsResponse,
  WorkflowFailureHandlingMode,
  Tag,
  TagObjectInAction,
  PublishedAttribute,
  ReasonCodeGroup,
  Action,
  List,
} from '../../engine/types';
import { updatablePublishedAttributeFields } from '~/dashboard/views/workflows/editor/components/NodeEditorDrawer/AttributeConfiguration/types';
import {
  OperationMessageType,
} from '../../engine/types';
import type { WorkflowState } from '../state';
import { compileWorkflowDataDependencies, snapshotAndCloneWorkflowData } from '../helpers';
import type { FeatureFlags } from '~/helpers/typescript';
import { WorkflowOperationType, ActionType } from '../../engine/constants';
import { transformWorkflowInitializationData } from '../utils/workflowTransformation';
import { createEphemeralTag, isTagEphemeral } from 'flow/service/tags/tags';
import { createSnapshot } from 'flow/service/history';
import { getTagActionsByTagName, isActionOfType } from 'flow/service/actions';
import { createEphemeralOutcome, isOutcomeEphemeral } from 'flow/service/outcomes/outcomes';
import { computeNextName } from 'flow/service/util';
import {
  createEphemeralPublishedAttribute,
  getActionsByPublishedAttributeToken,
  getPATokenFromAttributeAction,
  isPublishedAttributeEphemeral,
} from 'flow/service/publishedAttributes';
import { isReasonCodeGroupEphemeral } from 'flow/service/reasonCodes';
import { isListEphemeral } from 'flow/service/lists';

// ================ General Reducers ================

export function initWorkflowPropsAndData(
  state: WorkflowState,
  action: {
    payload: {
      workflowProps: WorkflowPropsResponse;
      workflowData: FlowState;
      featureFlags: Partial<FeatureFlags>;
    };
  }) {
  const {
    workflowData, workflowProps, featureFlags,
  } = action.payload;

  // Use the extracted transformation function
  const {
    initWorkflowProps,
    initWorkflowData,
    legacyMappings,
    ambiguousRuleErrors,
    initWorkflowFeatures,
    featureFlagsForStore,
  } = transformWorkflowInitializationData({
    workflowData,
    workflowProps,
    featureFlags,
  });

  // Update Redux state
  state.legacyMappings = legacyMappings;
  state.validation.ambiguousRuleErrors = ambiguousRuleErrors;
  state.workflowData = initWorkflowData;
  state.workflowProps = initWorkflowProps;
  state.workflowSnapshots = {
    workflowData: {
      history: {
        initial: createSnapshot({ data: initWorkflowData }),
        past: [],
      },
    },
    workflowProps: {
      history: {
        initial: createSnapshot({ data: initWorkflowProps }),
      },
    },
  };

  state.workflowFeatures = initWorkflowFeatures;

  // This is for adding relevant feature flags to the redux store,
  // specifically utilized for selectors/reducers/thunks (not components)
  state.featureFlagsForStore = featureFlagsForStore;
}

export function updateWorkflowProps(
  state: WorkflowState,
  action: { payload: Partial<WorkflowState['workflowProps']> },
) {
  const nextProps = action.payload;
  const {
    outcomes,
    publishedAttributes,
    reasonCodeGroups,
    tags,
    tagTypes,
    workflow,
  } = nextProps;

  /**
   * todo BUILD-817: this reducer should handle the computation of the Actions that
   * need to be updated based on incoming props. this is not supported yet, but will
   * be added once all account-level elements are supported and their needs are
   * more clear
   *
   * Redux wraps the local state in a Proxy to support immutable state updates with
   * mutable notation. The `current` function grabs the value that's within the Proxy.
   * https://redux-toolkit.js.org/api/other-exports#current
   */
  // const nextActions = computeActionsToUpdateWithNewProps(
  //   current(state.workflowData.actions),
  //   current(state.workflowProps),
  //   nextProps,
  // );
  // state.workflowData.actions = nextActions;
  // we explicity check against undefined to allow setting a value to `null`
  state.workflowProps = {
    ...state.workflowProps,
    ...(outcomes !== undefined && { outcomes }),
    ...(publishedAttributes !== undefined && { publishedAttributes }),
    ...(reasonCodeGroups !== undefined && { reasonCodeGroups }),
    ...(tags !== undefined && { tags }),
    ...(tagTypes !== undefined && { tagTypes }),
    ...(workflow !== undefined && { workflow }),
  };
}

export function updateDependencies(
  state: WorkflowState,
) {
  const regeneratedDependencies = buildDependencies({
    actions: state.workflowData.actions,
    thresholds: state.workflowData.thresholds,
    dataRoutes: state.workflowData.dataRoutes,
  });

  state.workflowData = {
    ...state.workflowData,
    dependencies: regeneratedDependencies,
  };
}

export function updateSdkConfig(
  state: WorkflowState,
  action: { payload: WorkflowState['workflowData']['sdkConfig'] },
) {
  state.workflowData.sdkConfig = action.payload;
}

export function updateServiceCredentialsInWorkflowProps(
  state: WorkflowState,
  action: {
    payload: {
      internalName: ServiceObject['internal_name'];
      credentials: ServiceCredentials;
    };
  },
) {
  const { credentials, internalName } = action.payload;
  state.workflowProps.services[internalName].credentialsSchema = credentials;
}

export function updateServiceConfigInWorkflowProps(
  state: WorkflowState,
  action: {
    payload: {
      internalName: ServiceObject['internal_name'];
      serviceConfig: ServiceConfig;
    };
  },
) {
  const { internalName, serviceConfig } = action.payload;
  state.workflowProps.services[internalName].serviceConfig = serviceConfig;
}

export function updateServiceActionValidation(
  state: WorkflowState,
  action: {
    payload: {
      publicId: string;
      data: string[];
    };
  },
) {
  const { publicId, data } = action.payload;
  if (publicId && state.workflowData.actions[publicId]) {
    state.workflowData.actions[publicId].validation = data;
  }
}

export function applyPrevWorkflowDataSnapshot(
  state: WorkflowState,
  action: {
    payload: {
      publicId?: string;
      data: any; // we literally would accept anything as of now
    };
  },
) {
  const { publicId, data } = action.payload;
  const pastHistoryLength = state.workflowSnapshots.workflowData.history.past.length;

  state.workflowData = pastHistoryLength <= 1
    ? state.workflowSnapshots.workflowData.history.initial.data
    : state.workflowSnapshots.workflowData.history.past.at(-2).data;

  const { workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  state.workflowSnapshots = workflowSnapshots;

  const newOperationMsg = {
    id: uuidv4(),
    publicId: publicId || '',
    type: OperationMessageType.undo,
    timestamp: Date.now(),
    message: WorkflowOperationType.UNDO_PERFORMED,
    data: data || undefined,
  };

  state.workflowOperationDescriptions.push(newOperationMsg);
}

export function dismissWorkflowOperationMessage(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
    };
  },
) {
  const { workflowOperationDescriptions } = state;
  const { id } = action.payload;

  const filteredOperationDesc = workflowOperationDescriptions.filter(descMsgObject => (
    descMsgObject.id !== id
    && (descMsgObject.timestamp - Date.now()) >= 300000), // anything beyond 5 mins ago will be cleared too
  );
  state.workflowOperationDescriptions = filteredOperationDesc;
}

export function createWorkflowOperationMessage(
  state: WorkflowState,
  action: {
    payload: {
      object: WorkflowOperationMessage;
    };
  },
) {
  const { object } = action.payload;

  state.workflowOperationDescriptions.push(object);
}

export function updateFailureHandlingMode(
  state: WorkflowState,
  action: {
    payload: {
      mode: WorkflowFailureHandlingMode;
    };
  },
) {
  const { mode } = action.payload;

  state.workflowData.failureHandlingMode = mode;
}

export function addEphemeralTagToProps(
  state: WorkflowState,
  action: {
    payload: Parameters<typeof createEphemeralTag>[0];
  },
) {
  const { name, ...restOpts } = action.payload;

  const existingTagNames = state.workflowProps?.tags?.map(existingTag => existingTag.tag_name);
  const uniqueTagNames = new Set<string>(existingTagNames);

  const ephemeralTag = createEphemeralTag({
    name: computeNextName(name, uniqueTagNames),
    ...restOpts,
  });

  state.workflowProps?.tags?.push(ephemeralTag);
}


/** @unused - it may come back, waiting to remove until ephemeral artifacts epic is over */
// TODO - BUILD-3923 - cleanup if still unused
export function removeEphemeralTagFromProps(
  state: WorkflowState,
  action: { payload: { name: string } },
) {
  const { name: ephemeralName } = action.payload;

  const isTagInWorkflow = getTagActionsByTagName(
    state.workflowData.actions,
    ephemeralName,
  ).length > 0;

  if (!isTagInWorkflow) {
    state.workflowProps.tags = state.workflowProps.tags.filter(
      tag => !(isTagEphemeral(tag) && tag.tag_name === ephemeralName),
    );
  }
}

export function updateTagInProps(
  state: WorkflowState,
  action: { payload: { updatedTagData: TagObjectInAction; actionId: string } },
) {
  const { payload: { updatedTagData, actionId } } = action;
  // find the current tag from UI (cannot rely on name of editedTag as that may have changed)
  const focusedAction = state.workflowData.actions[actionId];
  const matchingOutcomes = (updatedTagData.outcomes ?? [])
    .map(selectedOutcome => state.workflowProps.outcomes.find(
      outcome => outcome.outcome === selectedOutcome.outcome,
    ))
    .filter(Boolean);
  const matchingTagProp = state.workflowProps.tags.find(
    tag => tag.tag_name === focusedAction.tag.name,
  );
  let updatedTag: Tag;
  if (!isTagEphemeral(matchingTagProp)) {
    // using explicit matching here to avoid accidentally updating name or overwriting uneditable values
    updatedTag = {
      ...matchingTagProp,
      suppression_interval: updatedTagData.suppressionInterval,
      associated_types: updatedTagData.associatedTypes,
      is_case_creating: updatedTagData.isCaseCreating,
      is_adverse_action_reason: updatedTagData.isAdverseActionReason,
      category_name: updatedTagData.categoryName,
      category_id: updatedTagData.categoryId,
      archived: updatedTagData.archived,
      description: updatedTagData.description,
      tag_color: updatedTagData.color,
      is_dirty: true,
      outcomes: matchingOutcomes,
    };
  } else {
    // update tag in props - combine editedTag (converted to wf prop) w/matching tag from props
    updatedTag = {
      ...createEphemeralTag({ name: updatedTagData.name, ...updatedTagData }),
      app_version_count: matchingTagProp.app_version_count,
      customer_tag_id: matchingTagProp.customer_tag_id,
      outcomes: matchingOutcomes,
    };
  }
  // snapshot/clone wf data before making any changes
  const {
    workflowData, workflowSnapshots,
  } = snapshotAndCloneWorkflowData(state);

  const actionsToUpdate = getTagActionsByTagName(workflowData.actions, focusedAction.tag.name);
  // loop over all actions + update w/new tag data
  for (const actionToUpdate of actionsToUpdate) {
    actionToUpdate.tag = {
      ...updatedTagData,
      ...(isTagEphemeral(matchingTagProp)
        ? { isEphemeral: true }
        : { isDirty: true }),
    };
  }
  const updatedTagProps = [
    ...state.workflowProps.tags
      .filter(tag => tag.tag_name !== matchingTagProp.tag_name),
    updatedTag,
  ];

  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowProps = { ...state.workflowProps, tags: updatedTagProps };
  state.workflowSnapshots = workflowSnapshots;
}

export function addEphemeralOutcomeToProps(
  state: WorkflowState,
  action: {
    payload: Parameters<typeof createEphemeralOutcome>[0];
  },
) {
  const { name, ...restOpts } = action.payload;

  const existingOutcomeNames = (
    state.workflowProps?.outcomes?.map(existingOutcome => existingOutcome.outcome)
  );
  const uniqueOutcomeNames = new Set<string>(existingOutcomeNames);

  const ephemeralOutcome = createEphemeralOutcome({
    name: computeNextName(name, uniqueOutcomeNames),
    ...restOpts,
  });

  state.workflowProps?.outcomes?.push(ephemeralOutcome);
}
/** @unused - it may come back, waiting to remove until ephemeral artifacts epic is over */
// TODO - BUILD-3923 - cleanup if still unused
export function removeEphemeralOutcomeFromProps(
  state: WorkflowState,
  action: { payload: { name: string } },
) {
  const { name: ephemeralName } = action.payload;
  // TODO - BUILD-3836 - ACCOUNT FOR CUSTOMER OUTCOME TAGS IF NECESSARY
  state.workflowProps.outcomes = state.workflowProps.outcomes.filter(
    outcome => !(isOutcomeEphemeral(outcome) && outcome.outcome === ephemeralName));
}

export function addEphemeralPublishedAttributeToProps(
  state: WorkflowState,
  action: { payload: { attributeData: Partial<PublishedAttribute> } },
) {
  const { attributeData } = action.payload;
  const ephemeralPA = createEphemeralPublishedAttribute(attributeData);
  state.workflowProps.publishedAttributes.push(ephemeralPA);
}

export function updatePublishedAttributeInProps(
  state: WorkflowState,
  action: { payload: {
    updatedPublishedAttributeData: Partial<PublishedAttribute>;
    updatedActionData: Action;
    actionId: string;
  }; },
) {
  const { payload: { updatedPublishedAttributeData, updatedActionData, actionId } } = action;
  const focusedAction = state.workflowData.actions[actionId];
  const token = getPATokenFromAttributeAction(focusedAction);
  const matchingPublishedAttributeProp = state.workflowProps.publishedAttributes.find(
    pa => pa.attributeToken === token,
  );

  // Defensive filtering - only apply fields that are defined in updatablePublishedAttributeFields
  const editableUpdates = Object.fromEntries(
    Object.entries(updatedPublishedAttributeData).filter(
      ([key]) => updatablePublishedAttributeFields.includes(
        key as typeof updatablePublishedAttributeFields[number],
      ),
    ),
  ) as Partial<PublishedAttribute>;

  const updatedPublishedAtribute: PublishedAttribute = {
    ...matchingPublishedAttributeProp,
    ...editableUpdates,
    ...(isPublishedAttributeEphemeral(matchingPublishedAttributeProp)
      ? { isEphemeral: true }
      : { isDirty: true }),
  };
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  // PAs can be in WF up to 2x (as an input + as an output) - get all related actions
  const connectedActions = getActionsByPublishedAttributeToken(workflowData.actions, token);
  for (const actionToUpdate of connectedActions) {
    if (isActionOfType(actionToUpdate, ActionType.INPUT)) {
      actionToUpdate.input = {
        ...actionToUpdate.input,
        name: updatedPublishedAtribute.attributeName,
        publishedDetails: updatedPublishedAtribute,
        // update action-specific properties **only** if this is targeted action
        ...(actionToUpdate.publicId === updatedActionData.publicId && {
          jqFunction: updatedActionData.input.jqFunction,
          useJq: String(updatedActionData.input.useJq),
        }),
      };
    } else {
      actionToUpdate.output = {
        ...actionToUpdate.output,
        name: updatedPublishedAtribute.attributeName,
        publishedDetails: updatedPublishedAtribute,
        // update action-specific properties **only** if this is targeted action
        ...(actionToUpdate.publicId === updatedActionData.publicId && {
          defineValueRanges: updatedActionData.output.defineValueRanges,
          outputValueRangeOptions: updatedActionData.output.outputValueRangeOptions,
        }),
      };
      // update action-specific properties **only** if this is targeted action
      if (actionToUpdate.publicId === updatedActionData.publicId) {
        // CRA-specific action options - these include updated defaults for backward-compatibility
        actionToUpdate.options = updatedActionData.options;
      }
    }
  }
  const updatedPublishedAttributeProps = [
    ...state.workflowProps.publishedAttributes.filter(
      pa => pa.attributeToken !== matchingPublishedAttributeProp.attributeToken,
    ),
    updatedPublishedAtribute,
  ];

  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowProps = {
    ...state.workflowProps,
    publishedAttributes: updatedPublishedAttributeProps,
  };
  state.workflowSnapshots = workflowSnapshots;
}

/**
 * Adds an ephemeral ReasonCodeGroup to props.
 * This reducer, as opposed to the other addXEphemeralProps reducers, does not
 * call a factory function for the reasonCodeGroup, as there is no UI flow to create
 * one where only partial data is available - it will always be created using an existing
 * ReasonCodeGroup object
 */
export function addEphemeralReasonCodeGroupToProps(
  state: WorkflowState,
  action: { payload: { reasonCodeGroup: ReasonCodeGroup } },
) {
  const { reasonCodeGroup } = action.payload;

  if (reasonCodeGroup.isEphemeral) {
    state.workflowProps.reasonCodeGroups.push(reasonCodeGroup);
  }
}
/**
 * Accepts an ephemeral list and adds to props
 */
export function addEphemeralListToProps(
  state: WorkflowState,
  action: { payload: { list: List } },
) {
  const { list } = action.payload;

  if (isListEphemeral(list)) {
    state.workflowProps.lists.push(list);
  }
}

export function removeEphemeralReasonCodeGroupFromProps(
  state: WorkflowState,
  action: { payload: { reasonCodeGroup: ReasonCodeGroup } },
) {
  const { reasonCodeGroup: reasonCodeGroupToDelete } = action.payload;

  if (!isReasonCodeGroupEphemeral(reasonCodeGroupToDelete)) {
    return;
  }

  state.workflowProps.reasonCodeGroups = state.workflowProps.reasonCodeGroups
    .filter(reasonCodeGroup => reasonCodeGroup.id !== reasonCodeGroupToDelete.id);
}
/**
 * used when swapping tags/outcomes and the `updateAction` reducer is used
 * otherwise, `removeAction` reducer handles ephemeral prop cleanup
 */
export function removeEphemeralProp(
  state: WorkflowState,
  action: { payload: { name: string; type: ActionType } },
) {
  const { name: ephemeralName, type } = action.payload;
  switch (type) {
    case ActionType.TAG: {
      const isTagInWorkflow = getTagActionsByTagName(
        state.workflowData.actions,
        ephemeralName,
      ).length > 0;

      if (!isTagInWorkflow) {
        state.workflowProps.tags = state.workflowProps.tags.filter(
          tag => !(isTagEphemeral(tag) && tag.tag_name === ephemeralName),
        );
      }
      break;
    }
    case ActionType.OUTCOME: {
      // TODO - BUILD-3836 - ACCOUNT FOR CUSTOMER OUTCOME TAGS IF NECESSARY
      state.workflowProps.outcomes = state.workflowProps.outcomes.filter(
        outcome => !(isOutcomeEphemeral(outcome) && outcome.outcome === ephemeralName));
      break;
    }
  }
}
